import React, { FC, memo, useEffect, useState } from 'react';
import { Checkbox as AntdCheckbox, Divider } from 'antd';
import classNames from 'classnames';
import s from './index.module.scss';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import { Checkbox } from 'shared/ui/Checkbox';
import type { CheckboxProps as AntdCheckboxProps } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';

interface CheckboxGroupProps {
  options: Array<{ value: string | number; label: string | number }>;
  name: string;
  className?: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checkedValue: CheckboxValueType[]) => void;
  requiredMark?: boolean;
  value?: CheckboxValueType[];
  showSelectAll?: boolean;
}

export const CheckboxGroup: FC<CheckboxGroupProps> = memo((props) => {
  const { name, className, options, onChange, disabled, requiredMark, value, showSelectAll = true } = props;

  const { t } = useAppTranslation('common');

  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>(value || []);

  const currentCheckedList = value ?? checkedList;

  const checkAll = options.length > 0 && options.length === currentCheckedList.length;
  const indeterminate = currentCheckedList.length > 0 && currentCheckedList.length < options.length;

  useEffect(() => {
    if (value !== undefined) {
      setCheckedList(value);
    }
  }, [value]);

  const handleChange = (checkedValue: CheckboxValueType[]): void => {
    setCheckedList(checkedValue);
    onChange?.(checkedValue);
  };

  const onCheckAllChange: AntdCheckboxProps['onChange'] = (e) => {
    const isChecked = e.target.checked;
    const newCheckedList = isChecked ? options.map((option) => option.value) : [];
    setCheckedList(newCheckedList);
    onChange?.(newCheckedList);
  };

  return (
    <div className={showSelectAll ? undefined : 'contents'}>
      {showSelectAll && (
        <>
          <Checkbox name={name} indeterminate={indeterminate} onChangeEvent={onCheckAllChange} checked={checkAll}>
            <span>{t('Select all')}</span>
          </Checkbox>
          <Divider className="my-2" />
        </>
      )}
      <div className={classNames(showSelectAll ? undefined : 'contents', { 'flex items-center': requiredMark })}>
        {requiredMark && <div className="text-error text-2xl mr-1 pointer-events-none">*</div>}
        <AntdCheckbox.Group
          className={classNames(s.checkbox, className)}
          options={options}
          value={currentCheckedList}
          onChange={handleChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
});
